import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, withAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { usePlatformSettings } from "@/context/PlatformContext";
import { usePreview } from "@/context/PreviewContext";
import {
  SignOut, Sun, Moon, List, X, MagnifyingGlass, Bell, BellRinging, Megaphone, CaretDown, CaretRight, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { hasPerm } from "@/lib/permissions";
import { iconFor } from "@/lib/navIcons";

export default function Layout() {
  const { user, org, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const platform = usePlatformSettings();
  const { previewMode, setPreviewMode } = usePreview();
  const [open, setOpen] = useState(false);
  const [isPlatform, setIsPlatform] = useState(false);
  const [version, setVersion] = useState({ version: "1.0.0", copyright: "© 2026 Vyapar CRM. All Rights Reserved." });
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  // Pending + overdue follow-ups → drives the red dot on the sidebar entry.
  const [followupsDue, setFollowupsDue] = useState(0);
  // Server-driven nav (managed by Platform Admin in the Navigation Builder).
  const [navMenus, setNavMenus] = useState([]);
  const [expanded, setExpanded] = useState({});

  const loadNotifs = React.useCallback(async () => {
    try {
      const [u, l, fu] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/notifications", { params: { limit: 20 } }),
        api.get("/followups/stats/due").catch(() => ({ data: { count: 0 } })),
      ]);
      setUnread(u.data.count || 0);
      setNotifs(l.data);
      setFollowupsDue(fu.data?.count || 0);
    } catch { /* soft-fail */ }
  }, []);

  const loadNav = React.useCallback(async () => {
    try { const r = await api.get("/nav/menus"); setNavMenus(r.data); }
    catch { setNavMenus([]); }
  }, []);

  React.useEffect(() => {
    api.get("/version").then((r) => setVersion(r.data)).catch((err) => {
      if (process.env.NODE_ENV !== "production") console.warn("version fetch failed", err);
    });
    api.get("/platform/whoami").then((r) => setIsPlatform(r.data.is_platform_admin)).catch(() => {
      // non-platform-admin — this endpoint 404s by design, silence.
    });
    loadNotifs(); loadNav();
    const t = setInterval(loadNotifs, 60000);

    // ---- Realtime publish notifications ----
    // Prefer WebSocket for instant fan-out; fall back to a 20 s poll if the
    // socket fails (some proxies drop upgrade requests). Either way the
    // sidebar refetches nav + fires `vyparcrm:config-published` so any
    // config-driven component can subscribe.
    let ws = null;
    let pollTimer = null;
    let lastVersion = null;
    const onVersionBump = (payload) => {
      loadNav();
      window.dispatchEvent(new CustomEvent("vyparcrm:config-published", { detail: payload }));
    };

    const startPolling = () => {
      if (pollTimer) return;
      const tick = async () => {
        try {
          const r = await api.get("/config/version");
          if (lastVersion !== null && r.data.version !== lastVersion) onVersionBump(r.data);
          lastVersion = r.data.version;
        } catch { /* soft-fail */ }
      };
      tick();
      pollTimer = setInterval(tick, 20_000);
    };

    const openWs = () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return startPolling();
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/config?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "hello") { lastVersion = msg.global_version; return; }
            if (msg.type === "ping") return;
            if (msg.type === "publish" || msg.type === "rollback") {
              if (msg.global_version !== lastVersion) {
                lastVersion = msg.global_version;
                onVersionBump(msg);
              }
            }
          } catch { /* ignore malformed */ }
        };
        ws.onerror = () => { /* handled by onclose */ };
        ws.onclose = () => {
          ws = null;
          // Fall back to polling for the rest of this session
          startPolling();
        };
      } catch {
        startPolling();
      }
    };
    openWs();

    // Instant refetch when a publish happens in the current tab
    const onPublished = () => loadNav();
    window.addEventListener("vyparcrm:config-published", onPublished);
    return () => {
      clearInterval(t);
      if (pollTimer) clearInterval(pollTimer);
      if (ws) { try { ws.close(); } catch { /* ignore */ } }
      window.removeEventListener("vyparcrm:config-published", onPublished);
    };
  }, [loadNotifs, loadNav]);

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); loadNotifs(); }
    catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("mark-all-read failed", err);
    }
  };

  // Build the visible tree from the server-driven `navMenus` (managed by the
  // Platform Admin's Navigation Builder). Permission gating (`perm`) still
  // runs client-side to match the previous static behaviour; the server
  // already dropped platform_only / owner_only / role-restricted rows.
  const { topLevel, byParent } = useMemo(() => {
    const byId = {}; const byPar = {}; const top = [];
    const passes = (n) => !n.perm || hasPerm(user, n.perm);
    for (const n of navMenus) {
      if (!passes(n)) continue;
      byId[n.id] = n;
      if (n.parent_id && navMenus.some(x => x.id === n.parent_id)) {
        if (!byPar[n.parent_id]) byPar[n.parent_id] = [];
        byPar[n.parent_id].push(n);
      } else {
        top.push(n);
      }
    }
    top.sort((a, b) => a.order - b.order);
    Object.values(byPar).forEach(arr => arr.sort((a, b) => a.order - b.order));
    return { topLevel: top, byParent: byPar };
  }, [navMenus, user]);

  // Platform Admin's brand mark takes precedence — that's the wordmark the
  // whole SaaS displays. The org's own logo is the fallback (a firm can
  // upload one to override for its own users), and finally the bundled
  // default. Mirrors the priority used by Login.jsx.
  const brandLogo = platform?.logo_url || (org?.logo ? withAuth(org.logo) : "/logo.png");
  const brandName = org?.brand_name || org?.name || "Vyapar CRM";
  const brandTag = org?.brand_name && org?.name && org.brand_name !== org.name ? org.name : "Connect · Grow";

  return (
    <div className="min-h-screen flex bg-background">
      {previewMode && user?.is_platform_admin && (
        // Highly visible amber strip so nobody forgets they're looking at
        // draft state — makes it impossible to confuse preview with live.
        <div
          className="fixed top-0 left-0 right-0 z-[60] py-1.5 px-4 text-center text-xs font-semibold text-amber-950 bg-amber-400 border-b border-amber-600"
          data-testid="preview-mode-banner"
        >
          <Eye size={12} weight="fill" className="inline mr-1.5 -mt-0.5" />
          <b>Draft Preview</b> — you're seeing unpublished changes. Tenants still see the live version.
          <button
            onClick={() => setPreviewMode(false)}
            className="ml-3 underline hover:opacity-70"
            data-testid="preview-mode-exit"
          >
            Exit preview
          </button>
        </div>
      )}
      {platform.announcement_active && platform.announcement_text && (
        <div
          className={"fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-xs font-medium text-white "
            + (previewMode && user?.is_platform_admin ? "mt-8" : "")}
          style={{ background: platform.theme_color || "#4A2F1E" }}
          data-testid="announcement-bar"
        >
          <Megaphone size={12} weight="fill" className="inline mr-1.5 -mt-0.5" />
          {platform.announcement_text}
        </div>
      )}
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-64 border-r border-border bg-card flex flex-col transition-transform`}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src={brandLogo} alt={brandName} className="w-10 h-10 rounded-lg object-cover bg-white p-0.5 border border-border shrink-0" />
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-none tracking-tight truncate" title={brandName}>{brandName}</div>
              <div className="text-[10px] text-muted-foreground mt-1 tracking-[0.15em] uppercase truncate">{brandTag}</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {topLevel.map((n) => {
            const Ic = iconFor(n.icon);
            const kids = byParent[n.id] || [];
            const isOpen = expanded[n.id] ?? true;
            const hasKids = kids.length > 0;
            const key = n.label.toLowerCase().replace(/\s/g, "-");
            const badgeEl = n.badge === "followupsDue" && followupsDue > 0 ? (
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                data-testid="followups-due-badge"
                title={`${followupsDue} follow-ups due`}
              >
                {followupsDue > 99 ? "99+" : followupsDue}
              </span>
            ) : null;
            const linkClass = ({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`;
            // Group headers (no path) act as expand/collapse toggles.
            if (!n.path) {
              return (
                <div key={n.id}>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [n.id]: !isOpen }))}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
                    data-testid={`nav-${key}`}
                  >
                    <Ic size={18} weight="duotone" />
                    <span className="flex-1 text-left">{n.label}</span>
                    {hasKids && (isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />)}
                  </button>
                  {hasKids && isOpen && (
                    <div className="ml-4 space-y-1 mt-1">
                      {kids.map(c => {
                        const Ci = iconFor(c.icon);
                        return (
                          <NavLink key={c.id} to={c.path} end={c.path === "/"}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={linkClass}>
                            <Ci size={16} weight="duotone" />
                            <span className="flex-1">{c.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={n.id}>
                <div className="flex items-center gap-0">
                  <NavLink
                    to={n.path}
                    end={n.path === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${key}`}
                    className={({ isActive }) =>
                      `flex-1 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      }`
                    }
                  >
                    <Ic size={18} weight="duotone" />
                    <span className="flex-1">{n.label}</span>
                    {badgeEl}
                  </NavLink>
                  {hasKids && (
                    <button onClick={() => setExpanded(e => ({ ...e, [n.id]: !isOpen }))}
                      className="text-muted-foreground p-1.5 hover:bg-secondary rounded"
                      aria-label="Toggle submenu">
                      {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    </button>
                  )}
                </div>
                {hasKids && isOpen && (
                  <div className="ml-4 space-y-1 mt-1">
                    {kids.map(c => {
                      const Ci = iconFor(c.icon);
                      return (
                        <NavLink key={c.id} to={c.path || "#"} end={c.path === "/"}
                          onClick={() => setOpen(false)}
                          data-testid={`nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                          className={linkClass}>
                          <Ci size={16} weight="duotone" />
                          <span className="flex-1">{c.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.role?.replace("_"," ")}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button" className="w-full rounded-full">
            <SignOut size={14} className="mr-2" /> Sign out
          </Button>
          {user?.is_platform_admin && (
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={"w-full mt-2 flex items-center gap-2 justify-center px-3 py-2 rounded-full text-xs font-medium border transition-colors "
                + (previewMode
                  ? "bg-amber-400 text-amber-950 border-amber-600 hover:bg-amber-300"
                  : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground")}
              data-testid="preview-mode-toggle"
              title="Toggle Draft Preview — see unpublished Nav/Modules/Forms/UI/Templates changes"
            >
              {previewMode ? <EyeSlash size={13} weight="fill" /> : <Eye size={13} weight="duotone" />}
              {previewMode ? "Draft preview: ON" : "Draft preview: off"}
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 glass border-b border-border/60 h-14 px-4 lg:px-8 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
            <List size={22} />
          </button>
          <div className="flex-1 max-w-md relative hidden md:block">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search customers, orders, products…" className="pl-9 h-9 bg-background/60" data-testid="global-search" />
          </div>
          <div className="flex-1 md:hidden" />
          <div className="relative">
            <button onClick={() => setNotifOpen(!notifOpen)} data-testid="notif-bell"
              className="w-9 h-9 rounded-full border border-border hover:bg-secondary flex items-center justify-center relative">
              <Bell size={16} weight="duotone" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-bold rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="notif-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-11 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-card shadow-lg z-40" data-testid="notif-dropdown">
                <div className="flex items-center justify-between p-3 border-b border-border/60 sticky top-0 bg-card">
                  <div className="text-sm font-semibold">Notifications</div>
                  {unread > 0 && (
                    <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="notif-mark-all">Mark all read</button>
                  )}
                </div>
                {notifs.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</div>
                ) : notifs.map((n) => (
                  <div key={n.id} className={`p-3 border-b border-border/40 ${!n.read ? "bg-primary/5" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className="flex items-start gap-2">
                      <BellRinging size={14} weight="duotone" className={n.read ? "text-muted-foreground mt-0.5" : "text-primary mt-0.5"} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{n.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{n.body}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={toggle} data-testid="theme-toggle"
            className="w-9 h-9 rounded-full border border-border hover:bg-secondary flex items-center justify-center">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
        <footer className="border-t border-border/50 py-3 px-4 lg:px-8 text-[11px] text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-1" data-testid="app-footer">
          <div>{version.copyright}</div>
          <div>Vyapar CRM v{version.version}</div>
        </footer>
      </div>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
